import { useMemo } from 'react';
import { SubscriptionTier } from '@/actions/dtos/subscription';
import { BillingPeriod } from '@/types/billingPeriod';
import { formatCurrency } from '@/utils/currency';

const COUPON_DISCOUNT_RATE = 0.1; // todo: for now it's hardcoded to 10%
const PERIOD_DISCOUNT_PRO = 6.5;
const PERIOD_DISCOUNT_ULTIMATE = 15.85;

interface BasePrice {
  tier: SubscriptionTier;
  price: number;
}

const BASE_PRICES: BasePrice[] = [
  { tier: SubscriptionTier.BASIC, price: 0 }, // "Kostenlos"
  {
    tier: SubscriptionTier.PROFESSIONAL,
    price: 39.0,
  },
  {
    tier: SubscriptionTier.ULTIMATE,
    price: 89.0,
  },
  { tier: SubscriptionTier.ENTERPRISE, price: NaN }, // “Auf Anfrage”
];

export interface Pricing {
  basePrice: number;
  periodPrice: number;
  periodDiscount: number;
  couponDiscount: number;
  totalDiscount: number;
  couponDiscountRelative: number;
  totalPrice: number;
  formatted: {
    basePrice: string;
    periodPrice: string;
    totalDiscount?: string;
    totalPrice: string;
  };
}

export function getPricingDetails(
  tier: SubscriptionTier,
  period: BillingPeriod,
  discountCode?: string,
): Pricing | null {
  const priceEntry = BASE_PRICES.find((p) => p.tier === tier);
  if (!priceEntry) return null;

  const basePrice = priceEntry.price;

  let periodDiscount = 0;
  if (period === BillingPeriod.YEAR) {
    if (SubscriptionTier.PROFESSIONAL === tier) {
      periodDiscount = PERIOD_DISCOUNT_PRO;
    } else if (SubscriptionTier.ULTIMATE === tier) {
      periodDiscount = PERIOD_DISCOUNT_ULTIMATE;
    }
  }

  const periodPrice = basePrice - periodDiscount;

  const couponDiscount = discountCode ? Math.round(periodPrice * COUPON_DISCOUNT_RATE * 100) / 100 : 0;

  const totalDiscount = periodDiscount + couponDiscount;
  const totalPrice = basePrice - totalDiscount;

  return {
    basePrice,
    periodPrice,
    periodDiscount,
    couponDiscount,
    totalDiscount,
    couponDiscountRelative: COUPON_DISCOUNT_RATE * 100,
    totalPrice,
    formatted: {
      basePrice: formatCurrency(basePrice),
      periodPrice: formatCurrency(periodPrice),
      totalDiscount: totalDiscount > 0 ? `-${formatCurrency(totalDiscount)}` : undefined,
      totalPrice: formatCurrency(totalPrice),
    },
  };
}

export function usePricing(tier: SubscriptionTier | null, period: BillingPeriod | null, discountCode?: string) {
  return useMemo(() => {
    if (!tier || !period) {
      return { details: null, isLoading: true };
    }
    const details = getPricingDetails(tier, period, discountCode);
    return { details, isLoading: details === null };
  }, [tier, period, discountCode]);
}
